// Extracted from original/src/App.tsx:13663-13770 (the "layout" settings card, ADR-08 §2.1): theme toggle,
// Theme Studio button, theme colour, card size, "customize icons" — in that order, nothing else. One control is
// added (D-124; tests/visual DEVIATIONS.md V-05): the language toggle copied from the maintenance card
// (:16882-16902), placed last so every old control keeps its position. Display scale and dashboard icon size are
// NOT on the old card — they live in components/ThemeStudioModal.tsx:195-300, which arrives with Theme Studio in
// B4 (D-124) — so they are not shown here. Cut: setDarkMode / setThemeColor / setCardSize state and
// localStorage; the container writes `ui.*` preferences (D-123).
import { Languages, Moon, Palette, Sun } from 'lucide-react';
import type { Locale } from '@faylasuf/i18n';
import { useT } from '../i18n-context.js';
import { SettingsCardFrame } from './Settings.js';

export type IconSize = 'sm' | 'md' | 'lg' | 'xl';
export type CardSize = 'sm' | 'md' | 'lg';

/** App.tsx:13724-13733. */
export const THEME_COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#111827"] as const;

export interface AppearanceCardProps {
  readonly customIcon?: string;
  readonly darkMode: boolean;
  readonly onDarkModeChange: (dark: boolean) => void;
  readonly onThemeStudio?: () => void;
  readonly themeColor: string;
  readonly onThemeColorChange: (color: string) => void;
  readonly cardSize: CardSize;
  readonly onCardSizeChange: (size: CardSize) => void;
  readonly onCustomizeIcons?: () => void;
  readonly language: Locale;
  readonly onLanguageChange: (locale: Locale) => void;
}

export function AppearanceCard(props: AppearanceCardProps) {
  const t = useT();
  const { darkMode, themeColor, cardSize, language } = props;
  const labelClass = "block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest";
  return (
    <SettingsCardFrame cardKey="layout" {...(props.customIcon ? { customIcon: props.customIcon } : {})}>
      <div className="space-y-4">
        <div>
          <label className={labelClass}>
            {t('theme')}
          </label>
          <div className="flex gap-2 bg-gray-50 dark:bg-[#05070a] p-1 rounded-xl border border-gray-100 dark:border-[#30363d] mb-3">
            <button
              onClick={() => props.onDarkModeChange(false)}
              data-testid="appearance-light"
              className={`flex-1 py-2 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 ${!darkMode ? "bg-white dark:bg-[#0d1117] text-primary-500 shadow-sm border border-gray-100 dark:border-[#30363d]" : "text-gray-400 dark:text-[#8b949e] hover:text-gray-500"}`}
            >
              <Sun size={14} />
              {t('lightMode')}
            </button>
            <button
              onClick={() => props.onDarkModeChange(true)}
              data-testid="appearance-dark"
              className={`flex-1 py-2 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 ${darkMode ? "bg-white dark:bg-[#0d1117] text-primary-500 shadow-sm border border-gray-100 dark:border-[#30363d]" : "text-gray-400 dark:text-[#8b949e] hover:text-gray-500"}`}
            >
              <Moon size={14} />
              {t('darkMode')}
            </button>
          </div>
          {/* Theme Studio Button */}
          <button
            type="button"
            onClick={props.onThemeStudio}
            className="w-full py-3.5 bg-gradient-to-r from-primary-500 via-indigo-600 to-amber-500 hover:from-primary-600 hover:to-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-primary-500/20 flex items-center justify-center gap-2 active:scale-98 cursor-pointer"
          >
            <Palette size={18} />
            <span>
              {t('appearance.theme_studio')}
            </span>
          </button>
        </div>
        <div>
          <label className={labelClass}>
            {t('color')}
          </label>
          <div className="flex flex-wrap gap-2">
            {THEME_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => props.onThemeColorChange(color)}
                className={`w-8 h-8 rounded-full transition-all ${themeColor === color ? "scale-110 ring-2 ring-offset-2 ring-gray-200 dark:ring-[#30363d] shadow-lg" : "hover:scale-105 opacity-80 hover:opacity-100"}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
        <div>
          <label className={labelClass}>
            {t('size')}
          </label>
          <div className="flex gap-1 bg-gray-50 dark:bg-[#05070a] p-1 rounded-lg border border-gray-100 dark:border-[#30363d]">
            {(["sm", "md", "lg"] as const).map((size) => (
              <button
                key={size}
                onClick={() => props.onCardSizeChange(size)}
                className={`flex-1 py-1.5 rounded-md text-[10px] font-black transition-all ${cardSize === size ? "bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] shadow-sm border border-gray-100 dark:border-[#30363d]" : "text-gray-400 dark:text-[#8b949e] hover:text-gray-600"}`}
              >
                {size.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="pt-4 border-t border-gray-100 dark:border-[#30363d]">
          <button
            onClick={props.onCustomizeIcons}
            className="w-full py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all flex items-center justify-center gap-2"
          >
            <Palette size={14} />
            {t('customizeIcons')}
          </button>
        </div>
        {/* Language (App.tsx:16882-16902, moved here per D-124 — V-05; last so the old controls keep their place) */}
        <div data-testid="appearance-language">
          <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-3 uppercase tracking-widest flex items-center gap-2">
            <Languages size={14} />
            {t('lang')}
          </label>
          <div className="flex gap-2 bg-gray-50 dark:bg-[#05070a] p-1 rounded-xl border border-gray-100 dark:border-[#30363d]">
            <button
              onClick={() => props.onLanguageChange("ar")}
              data-testid="appearance-lang-ar"
              className={`flex-1 py-2.5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 ${language === "ar" ? "bg-white dark:bg-[#0d1117] text-blue-600 dark:text-blue-400 shadow-sm border border-gray-100 dark:border-[#30363d]" : "text-gray-400 dark:text-[#8b949e] hover:text-gray-500"}`}
            >
              <Languages size={16} />
              {t('appearance.arabic')}
            </button>
            <button
              onClick={() => props.onLanguageChange("en")}
              data-testid="appearance-lang-en"
              className={`flex-1 py-2.5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 ${language === "en" ? "bg-white dark:bg-[#0d1117] text-blue-600 dark:text-blue-400 shadow-sm border border-gray-100 dark:border-[#30363d]" : "text-gray-400 dark:text-[#8b949e] hover:text-gray-500"}`}
            >
              <Languages size={16} />
              {t('appearance.english')}
            </button>
          </div>
        </div>
      </div>
    </SettingsCardFrame>
  );
}
